import React, { useRef, useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Pencil, Trash2, Move, ZoomIn, ZoomOut, RotateCcw, 
  Check, X, Ruler, Palette
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.jsx";
import { getNextSectionName, STANDARD_SECTION_NAMES } from '@/utils/sectionNames';

const SECTION_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', 
  '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#14b8a6'
];

export default function ImageMarkup({ 
  imageUrl, 
  sections = [], 
  onSectionsChange,
  pixelsPerFoot = 10,
  readOnly = false 
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [currentLine, setCurrentLine] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [tool, setTool] = useState('draw');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [editingName, setEditingName] = useState(null);
  const [tempName, setTempName] = useState('');

  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
      setImageLoaded(true);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    if (!imageLoaded || !canvasRef.current) return;
    drawCanvas();
  }, [imageLoaded, sections, currentLine, zoom, pan, selectedSection]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.translate(pan.x, pan.y);
      ctx.scale(zoom, zoom);
      ctx.drawImage(img, 0, 0);
      
      // Draw sections
      sections.forEach((section, idx) => {
        const isSelected = selectedSection === idx;
        ctx.beginPath();
        ctx.moveTo(section.start_x, section.start_y);
        ctx.lineTo(section.end_x, section.end_y);
        ctx.strokeStyle = section.color || SECTION_COLORS[idx % SECTION_COLORS.length];
        ctx.lineWidth = isSelected ? 6 : 4;
        ctx.lineCap = 'round';
        ctx.stroke();
        
        // Draw endpoints
        [{ x: section.start_x, y: section.start_y }, { x: section.end_x, y: section.end_y }].forEach(point => {
          ctx.beginPath();
          ctx.arc(point.x, point.y, isSelected ? 8 : 6, 0, Math.PI * 2);
          ctx.fillStyle = section.color || SECTION_COLORS[idx % SECTION_COLORS.length];
          ctx.fill();
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.stroke();
        });
        
        // Draw letter label badge
        const midX = (section.start_x + section.end_x) / 2;
        const midY = (section.start_y + section.end_y) / 2;
        
        // Calculate perpendicular offset to position label above the line
        const dx = section.end_x - section.start_x;
        const dy = section.end_y - section.start_y;
        const lineLength = Math.sqrt(dx * dx + dy * dy);
        const normalX = -dy / lineLength;
        const normalY = dx / lineLength;
        const offsetDistance = 25;
        
        const labelX = midX + normalX * offsetDistance;
        const labelY = midY + normalY * offsetDistance;
        
        // Draw circular letter badge (A, B, C, etc.)
        const letter = String.fromCharCode(65 + idx);
        const badgeRadius = 16;
        
        ctx.beginPath();
        ctx.arc(labelX, labelY, badgeRadius, 0, Math.PI * 2);
        ctx.fillStyle = section.color || SECTION_COLORS[idx % SECTION_COLORS.length];
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        ctx.font = 'bold 16px Inter, sans-serif';
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(letter, labelX, labelY);
      });
      
      // Draw current line
      if (currentLine) {
        ctx.beginPath();
        ctx.moveTo(currentLine.start_x, currentLine.start_y);
        ctx.lineTo(currentLine.end_x, currentLine.end_y);
        ctx.strokeStyle = SECTION_COLORS[sections.length % SECTION_COLORS.length];
        ctx.lineWidth = 4;
        ctx.setLineDash([8, 4]);
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.setLineDash([]);
      }
      
      ctx.restore();
    };
    img.src = imageUrl;
  };

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Handle both mouse and touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: ((clientX - rect.left) * scaleX - pan.x) / zoom,
      y: ((clientY - rect.top) * scaleY - pan.y) / zoom
    };
  };

  const handleStart = (e) => {
    if (readOnly) return;
    e.preventDefault(); // Prevent scrolling on touch devices
    const coords = getCanvasCoords(e);
    
    if (tool === 'draw') {
      setDrawing(true);
      setCurrentLine({ start_x: coords.x, start_y: coords.y, end_x: coords.x, end_y: coords.y });
    } else if (tool === 'select') {
      const clickedIdx = sections.findIndex(section => {
        const dist = pointToLineDistance(coords, section);
        return dist < 15;
      });
      setSelectedSection(clickedIdx >= 0 ? clickedIdx : null);
    }
  };

  const handleMove = (e) => {
    if (!drawing || readOnly) return;
    e.preventDefault(); // Prevent scrolling on touch devices
    const coords = getCanvasCoords(e);
    setCurrentLine(prev => ({ ...prev, end_x: coords.x, end_y: coords.y }));
  };

  const handleEnd = () => {
    if (!drawing || !currentLine || readOnly) return;
    setDrawing(false);
    
    const length = Math.sqrt(
      Math.pow(currentLine.end_x - currentLine.start_x, 2) + 
      Math.pow(currentLine.end_y - currentLine.start_y, 2)
    );
    
    if (length > 20) {
      const lengthFeet = Math.round((length / pixelsPerFoot) * 10) / 10;
      const lineData = { ...currentLine, length_feet: lengthFeet };
      const newSection = {
        id: `section-${Date.now()}`,
        name: getNextSectionName(sections, lineData),
        ...currentLine,
        length_feet: lengthFeet,
        puck_count: 0,
        color: SECTION_COLORS[sections.length % SECTION_COLORS.length],
        notes: ''
      };
      onSectionsChange([...sections, newSection]);
    }
    setCurrentLine(null);
  };

  // Keep old handlers for backward compatibility
  const handleMouseDown = handleStart;
  const handleMouseMove = handleMove;
  const handleMouseUp = handleEnd;

  const pointToLineDistance = (point, line) => {
    const A = point.x - line.start_x;
    const B = point.y - line.start_y;
    const C = line.end_x - line.start_x;
    const D = line.end_y - line.start_y;
    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    if (lenSq !== 0) param = dot / lenSq;
    let xx, yy;
    if (param < 0) { xx = line.start_x; yy = line.start_y; }
    else if (param > 1) { xx = line.end_x; yy = line.end_y; }
    else { xx = line.start_x + param * C; yy = line.start_y + param * D; }
    const dx = point.x - xx;
    const dy = point.y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const deleteSection = (idx) => {
    const newSections = sections.filter((_, i) => i !== idx);
    onSectionsChange(newSections);
    setSelectedSection(null);
  };

  const updateSectionName = (idx) => {
    if (!tempName.trim()) return;
    const newSections = [...sections];
    newSections[idx] = { ...newSections[idx], name: tempName };
    onSectionsChange(newSections);
    setEditingName(null);
    setTempName('');
  };

  const updateSectionColor = (idx, color) => {
    const newSections = [...sections];
    newSections[idx] = { ...newSections[idx], color };
    onSectionsChange(newSections);
  };

  const updateSectionLength = (idx, length) => {
    const newSections = [...sections];
    newSections[idx] = { ...newSections[idx], length_feet: parseFloat(length) || 0 };
    onSectionsChange(newSections);
  };

  if (!imageUrl) {
    return (
      <div className="flex items-center justify-center h-64 bg-slate-100 rounded-xl border-2 border-dashed border-slate-300">
        <p className="text-slate-500">Upload an image to begin markup</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2 p-3 bg-slate-50 rounded-xl">
          <div className="flex items-center gap-1 p-1 bg-white rounded-lg shadow-sm">
            <Button
              size="sm"
              variant={tool === 'draw' ? 'default' : 'ghost'}
              onClick={() => setTool('draw')}
              className="h-8"
            >
              <Pencil className="w-4 h-4 mr-1" />
              Draw
            </Button>
            <Button
              size="sm"
              variant={tool === 'select' ? 'default' : 'ghost'}
              onClick={() => setTool('select')}
              className="h-8"
            >
              <Move className="w-4 h-4 mr-1" />
              Select
            </Button>
          </div>
          
          <div className="h-6 w-px bg-slate-300" />
          
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" onClick={() => setZoom(z => Math.min(z + 0.25, 3))} className="h-8 w-8">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600 w-12 text-center">{Math.round(zoom * 100)}%</span>
            <Button size="icon" variant="ghost" onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} className="h-8 w-8">
              <ZoomOut className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="h-6 w-px bg-slate-300" />
          
          <Button size="sm" variant="ghost" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="h-8">
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
          
          {selectedSection !== null && (
            <>
              <div className="h-6 w-px bg-slate-300" />
              <Button size="sm" variant="destructive" onClick={() => deleteSection(selectedSection)} className="h-8">
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </>
          )}
        </div>
      )}
      
      <div ref={containerRef} className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-900">
        <canvas
          ref={canvasRef}
          width={imageDimensions.width || 800}
          height={imageDimensions.height || 600}
          className="max-w-full h-auto cursor-crosshair touch-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
      </div>
      
      {sections.length > 0 && (
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Section Legend</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section, idx) => (
              <div key={section.id} className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: section.color || SECTION_COLORS[idx % SECTION_COLORS.length] }}
                >
                  {String.fromCharCode(65 + idx)}
                </div>
                <span className="text-sm text-slate-700 font-medium">{section.name}</span>
                <span className="text-xs text-slate-500 ml-auto">{section.length_feet} ft</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {sections.length > 0 && !readOnly && (
        <div className="grid gap-2">
          <Label className="text-sm font-medium text-slate-700">Sections</Label>
          <div className="grid gap-2">
            {sections.map((section, idx) => (
              <div 
                key={section.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                  selectedSection === idx 
                    ? 'border-amber-400 bg-amber-50' 
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                onClick={() => setSelectedSection(idx)}
              >
                <div 
                  className="w-4 h-4 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: section.color }}
                />
                
                {editingName === idx ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Select
                      value={tempName}
                      onValueChange={(value) => {
                        setTempName(value);
                        const newSections = [...sections];
                        newSections[idx] = { ...newSections[idx], name: value };
                        onSectionsChange(newSections);
                        setEditingName(null);
                        setTempName('');
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm flex-1">
                        <SelectValue placeholder="Select or type name..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {STANDARD_SECTION_NAMES.map(name => (
                          <SelectItem key={name} value={name} className="text-sm">
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      className="h-8 text-sm flex-1"
                      placeholder="Or type custom name..."
                      onKeyDown={(e) => e.key === 'Enter' && updateSectionName(idx)}
                    />
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => updateSectionName(idx)}>
                      <Check className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingName(null)}>
                      <X className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <span 
                      className="font-medium text-slate-900 cursor-pointer hover:text-amber-600 flex-1"
                      onClick={(e) => { e.stopPropagation(); setEditingName(idx); setTempName(section.name); }}
                    >
                      {section.name}
                    </span>
                    
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Ruler className="w-3 h-3 text-slate-400" />
                        <Input
                          type="number"
                          value={section.length_feet}
                          onChange={(e) => updateSectionLength(idx, e.target.value)}
                          className="h-7 w-16 text-sm text-center"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-xs text-slate-500">ft</span>
                      </div>
                      
                      <Popover>
                        <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button size="icon" variant="ghost" className="h-7 w-7">
                            <Palette className="w-3 h-3" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2">
                          <div className="grid grid-cols-5 gap-1">
                            {SECTION_COLORS.map(color => (
                              <button
                                key={color}
                                className="w-6 h-6 rounded-full hover:scale-110 transition-transform"
                                style={{ backgroundColor: color }}
                                onClick={() => updateSectionColor(idx, color)}
                              />
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-7 w-7 text-red-500 hover:text-red-700"
                        onClick={(e) => { e.stopPropagation(); deleteSection(idx); }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}